import { journalEntryContract } from "@common/contracts/journal-entry.contract";
import { JournalEntry } from "@common/types/domains/accounting/journalEntry";
import { GetManualJournalEntriesDto } from "@common/dto/manualJournalEntry/get-manual-journal-entries.dto";
import { ManualJournalRecurringUpdate } from "@common/types/domains/accounting/manualJournalRecurringRule";
import {
    AccountingLedgerBasis,
    BOTH_LEDGER_BASIS,
} from "@common/types/domains/accounting/ledger";
import {
    ManualJournalMutationResponseSchema,
    getManualJournalEntriesResponseSchema,
} from "@common/schemas/domains/accounting/journalEntry";
import { CreateManualJournalEntryDto } from "@common/dto/manualJournalEntry/create-manual-journal-entry.dto";
import { z } from "zod";
import { getBackendAPIClient } from "./backendAPIClient";

type ManualJournalMutationResponse = z.infer<
    typeof ManualJournalMutationResponseSchema
>;

const journalEntryContractClient = getBackendAPIClient(journalEntryContract);

export async function createManualJournalEntry(
    entityId: number,
    data: CreateManualJournalEntryDto,
): Promise<ManualJournalMutationResponse> {
    const result = await journalEntryContractClient.createManualJournalEntry({
        params: { entityId },
        body: data,
    });
    return ManualJournalMutationResponseSchema.parse(result.body);
}

export async function getManualJournalEntries(
    entityId: number,
    params: GetManualJournalEntriesDto,
): Promise<JournalEntry[]> {
    const result = await journalEntryContractClient.getManualJournalEntries({
        params: { entityId },
        query: params,
    });
    return getManualJournalEntriesResponseSchema.parse(result.body)
        .manualJournalEntries;
}

export async function upsertJournalEntry(
    entityId: number,
    journalEntryId: string,
    data: CreateManualJournalEntryDto,
): Promise<ManualJournalMutationResponse> {
    if (data.ledgerBasis === BOTH_LEDGER_BASIS) {
        throw new Error("Cannot use both ledger basis for update");
    }
    const result = await journalEntryContractClient.updateManualJournalEntry({
        params: { entityId, journalEntryId },
        body: {
            ...data,
            ledgerBasis: data.ledgerBasis,
        },
    });
    return ManualJournalMutationResponseSchema.parse(result.body);
}

interface DeleteJournalEntryParams {
    entityId: number;
    journalEntryId: string;
    recurringRuleUpdateType?: ManualJournalRecurringUpdate;
    ledgerBasis: AccountingLedgerBasis;
    ledgerId?: string;
}
export async function deleteJournalEntry({
    entityId,
    journalEntryId,
    recurringRuleUpdateType,
    ledgerBasis,
    ledgerId,
}: DeleteJournalEntryParams): Promise<void> {
    await journalEntryContractClient.deleteManualJournalEntry({
        params: { entityId, journalEntryId },
        body: { recurringRuleUpdateType, ledgerBasis, ledgerId },
    });
}
